// frontend/src/components/ReviewPage/MetricArmChart.tsx
import { Box, Stack, Typography } from '@mui/material'
import type { FanoutSlice } from '~/api/hooks/useTimeframeFanout'
import { TimeframeBarChart } from '~/components/Charts/TimeframeBarChart'
import { RankedDimBarChart } from '~/components/Charts/RankedDimBarChart'
import { toTimeframeBars } from '~/components/Charts/toTimeframeBars'
import { toRankedDimRows, isGrouped } from '~/components/Charts/toRankedDimRows'

interface Props {
  label: string
  slices: FanoutSlice[]
  colorForKey: (key: string) => string
  labelForKey: (key: string) => string
  topN?: number
  /** Shared [min, max] domain (Task 9/10) — threaded straight to RankedDimBarChart. */
  domain?: [number, number]
}

/** One CP arm. Mode is derived from the resolved data (data-derived so it works for both
 *  the live tile's stripToFactDims path and pin replay): ungrouped → per-timeframe bars;
 *  grouped + 1 TF → ranked rows; grouped + N TFs → small multiples (one ranked per TF). */
export function MetricArmChart({ label, slices, colorForKey, labelForKey, topN, domain }: Props) {
  if (!isGrouped(slices)) {
    return (
      <TimeframeBarChart title={label} {...toTimeframeBars(slices)} seriesLabelFor={labelForKey} />
    )
  }
  if (slices.length === 1) {
    return (
      <RankedDimBarChart
        title={label}
        rows={toRankedDimRows(slices[0]!)}
        colorForKey={colorForKey}
        labelForKey={labelForKey}
        topN={topN}
        domain={domain}
      />
    )
  }
  return (
    <Box>
      <Typography variant="subtitle2" align="center">
        {label}
      </Typography>
      <Stack direction="row" flexWrap="wrap" spacing={2} useFlexGap>
        {slices.map((s) => (
          <Box key={s.timeframe.id} sx={{ flex: '1 1 280px', minWidth: 0 }}>
            <RankedDimBarChart
              title={s.timeframe.label}
              rows={toRankedDimRows(s)}
              colorForKey={colorForKey}
              labelForKey={labelForKey}
              topN={topN}
              domain={domain}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
